import { formatDateOnlyLocal } from '@erp/shared-utils';

import { flaggedRowsToCsv } from './flaggedRowsToCsv';
import { rdStockRowsToCsv } from './rdStockRowsToCsv';
import {
  suggestedDownloadFileName,
  uniqueDivisionSegment,
} from './suggestedDownloadFileName';
import type {
  DailyReportSummary,
  FlaggedPotfUpcRow,
  FlaggedRdStockRow,
  FlaggedSuspendedRow,
  FlaggedWebNoPriceRow,
} from './types';
import {
  potfUpcRowsToCsv,
  vendorItemListRowsToCsv,
  webNoPriceRowsToCsv,
} from './worklistRowsToCsv';

export interface FlaggedDownloadFile {
  body: string;
  fileName: string;
}

function downloadFileFromRows<T>(args: {
  rows: readonly T[];
  reportType: string;
  date: string;
  divisionOf: (row: T) => string;
  toCsv: (rows: readonly T[]) => string;
}): FlaggedDownloadFile | null {
  if (args.rows.length === 0) return null;
  return {
    body: args.toCsv(args.rows),
    fileName: suggestedDownloadFileName({
      reportType: args.reportType,
      division: uniqueDivisionSegment(args.rows.map(args.divisionOf)),
      date: args.date,
    }),
  };
}

function rdDivision(row: FlaggedRdStockRow): string {
  return `${row.company}${row.division}`;
}

function suspendedDivision(row: FlaggedSuspendedRow): string {
  return row.div;
}

function webDivision(row: FlaggedWebNoPriceRow): string {
  return row.div;
}

function potfDivision(row: FlaggedPotfUpcRow): string {
  return row.otherDivisions;
}

function vendorItemListDivision(): string {
  return '';
}

/**
 * One CSV per report kind. Never concatenate Suspended Item and STOCK_V
 * schemas into a single file (two headers / different columns).
 */
export function flaggedDownloadFiles(
  summary: Pick<DailyReportSummary, 'flagged' | 'rdStockMapped'> &
    Partial<
      Pick<
        DailyReportSummary,
        | 'rdStockUnmatched'
        | 'supersededMapped'
        | 'supersededUnmatched'
        | 'webNoPrice'
        | 'potfUpc'
        | 'vendorItemList'
      >
    >,
  options?: { date?: string },
): FlaggedDownloadFile[] {
  const date = options?.date ?? formatDateOnlyLocal(new Date());
  return [
    downloadFileFromRows({
      rows: summary.flagged,
      reportType: 'suspended-item',
      date,
      divisionOf: suspendedDivision,
      toCsv: flaggedRowsToCsv,
    }),
    downloadFileFromRows({
      rows: summary.rdStockMapped,
      reportType: 'rd-stock-without-item',
      date,
      divisionOf: rdDivision,
      toCsv: rdStockRowsToCsv,
    }),
    downloadFileFromRows({
      rows: summary.rdStockUnmatched ?? [],
      reportType: 'rd-stock-unmatched',
      date,
      divisionOf: rdDivision,
      toCsv: rdStockRowsToCsv,
    }),
    downloadFileFromRows({
      rows: summary.supersededMapped ?? [],
      reportType: 'superseded-with-stock',
      date,
      divisionOf: rdDivision,
      toCsv: rdStockRowsToCsv,
    }),
    downloadFileFromRows({
      rows: summary.supersededUnmatched ?? [],
      reportType: 'superseded-with-stock-unmatched',
      date,
      divisionOf: rdDivision,
      toCsv: rdStockRowsToCsv,
    }),
    downloadFileFromRows({
      rows: summary.webNoPrice ?? [],
      reportType: 'web-no-price',
      date,
      divisionOf: webDivision,
      toCsv: webNoPriceRowsToCsv,
    }),
    downloadFileFromRows({
      rows: summary.potfUpc ?? [],
      reportType: 'potf-upc',
      date,
      divisionOf: potfDivision,
      toCsv: potfUpcRowsToCsv,
    }),
    downloadFileFromRows({
      rows: summary.vendorItemList ?? [],
      reportType: 'vendor-item-list-upload',
      date,
      divisionOf: vendorItemListDivision,
      toCsv: vendorItemListRowsToCsv,
    }),
  ].filter((file) => file !== null);
}
